import axios from "axios";
import { endpoints } from "./endpoints";

const fetchWithTimeout = async (url, options = {}, timeoutMs = 2000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

// Function to get user's location information
const getUserLocation = async () => {
  try {
    // Try to get location from browser's geolocation API first
    if (navigator.geolocation) {
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              // Use reverse geocoding to get city, state, country
              const { latitude, longitude } = position.coords;
              // Use native fetch to avoid axios interceptor
              const response = await fetchWithTimeout(
                `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
                {
                  method: "GET",
                  headers: {
                    Accept: "application/json",
                  },
                },
              );
              const data = await response.json();

              resolve({
                city: data.city || data.locality || "Unknown",
                state: data.principalSubdivision || data.region || "Unknown",
                country: data.countryName || "Unknown",
              });
            } catch (error) {
              console.warn(
                "Reverse geocoding failed, falling back to IP location",
              );
              resolve(await getLocationFromIP());
            }
          },
          async (error) => {
            console.warn("Geolocation denied, falling back to IP location");
            resolve(await getLocationFromIP());
          },
          { timeout: 5000 },
        );
      });
    } else {
      return await getLocationFromIP();
    }
  } catch (error) {
    console.warn("Location detection failed:", error);
    return {
      city: "Unknown",
      state: "Unknown",
      country: "Unknown",
    };
  }
};

// Fallback function to get location from IP
const getLocationFromIP = async () => {
  try {
    // Use native fetch to avoid axios interceptor
    const response = await fetchWithTimeout("https://ipapi.co/json/", {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });
    const data = await response.json();

    return {
      city: data.city || "Unknown",
      state: data.region || "Unknown",
      country: data.country_name || "Unknown",
    };
  } catch (error) {
    console.warn("IP location detection failed:", error);
    return {
      city: "Unknown",
      state: "Unknown",
      country: "Unknown",
    };
  }
};

// Store location data
let locationData = null;
let locationPromise = null;

// Initialize location detection
const initializeLocation = () => {
  if (!locationPromise) {
    locationPromise = getUserLocation().then((data) => {
      locationData = data;
      return data;
    });
  }
  return locationPromise;
};

// Set up axios request interceptor
axios.interceptors.request.use(
  (config) => {
    // Only add location headers to requests to your API endpoints
    // Check if this is a request to your backend API (exclude external services)
    const isInternalApiRequest =
      config.url &&
      ((endpoints.MITCO_API_URL &&
        config.url.includes(endpoints.MITCO_API_URL)) ||
        (process.env.REACT_APP_API_URL &&
          config.url.includes(process.env.REACT_APP_API_URL)) ||
        (config.url.startsWith("/") &&
          (config.url.startsWith("/api/") ||
            config.url.startsWith("/v1/") ||
            config.url.includes("/csfa/") ||
            config.url.includes("/users/") ||
            config.url.includes("/credits/") ||
            config.url.includes("/invoices/") ||
            config.url.includes("/logs/"))) ||
        // Also check for your development server IP pattern and common backend patterns
        config.url.includes("192.168.") ||
        config.url.includes("localhost") ||
        config.url.includes("127.0.0.1") ||
        config.url.includes(":8000") ||
        config.url.includes("/v1/") ||
        config.url.includes("/api/") ||
        config.url.includes("/csfa/"));

    // Exclude external geolocation and IP services
    const isExternalService =
      config.url &&
      (config.url.includes("bigdatacloud.net") ||
        config.url.includes("ipapi.co"));

    if (isInternalApiRequest && !isExternalService) {
      // Location is telemetry, not a prerequisite for the request. Never hold
      // checkout behind browser geolocation or a third-party reverse-geocoder.
      const requestLocation = locationData || {
        city: "Unknown",
        state: "Unknown",
        country: "Unknown",
      };
      config.headers = {
        ...config.headers,
        "X-User-City": requestLocation.city,
        "X-User-State": requestLocation.state,
        "X-User-Country": requestLocation.country,
      };
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Initialize location detection when the module loads
initializeLocation();

export { getUserLocation, initializeLocation };
